import React, { useState } from 'react';
import {
  registerCustomerApp,
  resetCustomerAppPassword,
  signInCustomerApp,
} from '../../lib/customerAppAuth';

type Mode = 'signIn' | 'register' | 'reset';

interface CustomerAppAuthPanelProps {
  onAuthenticated: () => void;
  initialEmail?: string;
  initialName?: string;
}

/**
 * Login / register / forgot-password for kingspark-app Auth (shared with Android).
 */
const CustomerAppAuthPanel: React.FC<CustomerAppAuthPanelProps> = ({
  onAuthenticated,
  initialEmail = '',
  initialName = '',
}) => {
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === 'signIn') {
        await signInCustomerApp(email, password);
        onAuthenticated();
      } else if (mode === 'register') {
        if (!displayName.trim()) {
          setError('Please enter your name.');
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters.');
          return;
        }
        await registerCustomerApp({
          email,
          password,
          displayName,
          phone: phone.trim() || undefined,
        });
        onAuthenticated();
      } else {
        await resetCustomerAppPassword(email);
        setInfo('Password reset email sent. Check your inbox.');
        setMode('signIn');
      }
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)\s*$/, '')
          : 'Authentication failed.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        {mode === 'signIn' ? 'Sign in to order' : mode === 'register' ? 'Create an account' : 'Reset password'}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Use the same account as the Kings Park mobile app. Sign in or register to continue checkout.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{error}</div>
      )}
      {info && (
        <div className="mb-4 p-3 bg-green-50 border border-green-300 text-green-800 rounded text-sm">{info}</div>
      )}

      <form onSubmit={submit} className="space-y-4">
        {mode === 'register' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        {mode !== 'reset' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              minLength={mode === 'register' ? 6 : undefined}
            />
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full py-3 rounded-md bg-pink-600 text-white font-semibold hover:bg-pink-700 disabled:opacity-60"
        >
          {busy
            ? 'Please wait…'
            : mode === 'signIn'
              ? 'Sign in'
              : mode === 'register'
                ? 'Register'
                : 'Send reset email'}
        </button>
      </form>

      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        {mode !== 'signIn' && (
          <button type="button" className="text-pink-600 hover:underline" onClick={() => setMode('signIn')}>
            Sign in
          </button>
        )}
        {mode !== 'register' && (
          <button type="button" className="text-pink-600 hover:underline" onClick={() => setMode('register')}>
            Create account
          </button>
        )}
        {mode !== 'reset' && (
          <button type="button" className="text-gray-600 hover:underline" onClick={() => setMode('reset')}>
            Forgot password?
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomerAppAuthPanel;
